import {StreamClient, ImmediateCheckError, SubscribeRejectedError} from './stream-client.library';
import type {JobReady} from './stream-client.library';
import {CentrifugoStreamClient} from './centrifugo-stream-client.library';

/** Result returned by {@link tryStreamPollingFastPath}. */
export type FastPathResult<R> = {handled: true; result: R} | {handled: false};

/**
 * The transport-blind client seam the fast path consumes. Satisfied by both
 * the SSE {@link StreamClient} and the {@link CentrifugoStreamClient}; which
 * one the default factory builds is selected by PMW.streamTransport.
 */
export interface RealtimeClient {
  warm: () => void;
  isConnected: () => boolean;
  awaitJob: (key: string, immediateCheck: () => Promise<JobReady | null>) => Promise<JobReady>;
  cancelJob: (key: string) => void;
}

/** Factory that produces a {@link RealtimeClient}. Overridable for tests. */
export type StreamClientFactory = () => RealtimeClient;

/** Module-singleton client, lazily constructed when the fast path is first used. */
let singletonClient: RealtimeClient | null = null;

/** Build (or reuse) the module singleton {@link RealtimeClient}. */
function getOrCreateClient(factory: StreamClientFactory): RealtimeClient {
  singletonClient ??= factory();
  return singletonClient;
}

const getStreamToken = (): Promise<{token: string; ns: string}> =>
  window.PMW.readLocal('stream/getStreamToken') as Promise<{token: string; ns: string}>;

/**
 * Default factory: constructs the client for the environment's active
 * transport (PMW.streamTransport) using production seams sourced from
 * {@link window.PMW} and browser globals.
 */
const defaultFactory: StreamClientFactory = (): RealtimeClient =>
  window.PMW.streamTransport === 'centrifugo'
    ? new CentrifugoStreamClient({getToken: getStreamToken})
    : new StreamClient({
        getToken: getStreamToken,
        eventSourceFactory: (url: string) => new EventSource(url, {withCredentials: true}),
        fetchFn: (...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => window.fetch(...args),
      });

/**
 * Proactively warms the SSE fast-path connection so an imminent pollable job
 * (e.g. a bulk image upload) reuses an open gateway connection instead of racing a
 * cold handshake against the 5s connect budget. No-op when the gateway is flag-off.
 * Safe to call repeatedly; never throws. `clientFactory` is a test seam.
 */
export function warmStreamFastPath(clientFactory: StreamClientFactory = defaultFactory): void {
  if (!window.PMW.streamGatewayEnabled) {
    return;
  }
  try {
    getOrCreateClient(clientFactory).warm();
  } catch {
    // swallow: warming must never affect the caller
  }
}

/** Narrow `initialData` to an object with a string `pollingKey`. */
function extractPollingKey(initialData: unknown): string | null {
  if (typeof initialData !== 'object' || initialData === null) {
    return null;
  }
  const record = initialData as Record<string, unknown>;
  const key = record.pollingKey;
  return typeof key === 'string' ? key : null;
}

/** Sentinel symbol used to distinguish a bail result from a real {@link JobReady}. */
const BAIL = Symbol('bail');

/** Why the fast path stopped waiting. `cancel` is a deliberate stop, not a failure. */
type BailReason = 'timeout' | 'cancel';

/** Overall SSE-wait budget when a caller doesn't specify one (matches the legacy loop default). */
const DEFAULT_FAST_PATH_TIMEOUT_MS = 1_800_000;

/**
 * How long to wait for the SSE gateway to CONNECT before bailing to the legacy poll loop.
 * Deliberately short: a healthy handshake completes in well under a second, while a
 * connection that silently stalls (buffering proxy, blocked `text/event-stream`) fires
 * neither `connected` nor `error`, so this timer is the only thing that ends the wait for
 * that client. Distinct from the total `timeout`: once connected we wait the full budget
 * for the ready event (a job may legitimately take minutes), but a connection that never
 * establishes bails here so the legacy fallback still has budget left to serve the result.
 */
export const SSE_CONNECT_TIMEOUT_MS = 5_000;

/** Tunables for {@link tryStreamPollingFastPath}. All optional; production defaults applied. */
export interface FastPathOptions {
  /** Overall wall-clock budget for the SSE wait before bailing. Defaults to {@link DEFAULT_FAST_PATH_TIMEOUT_MS}. */
  timeout?: number;
  /** Connect-phase budget; bails early if not connected by then. Clamped to never exceed {@link FastPathOptions.timeout}. Defaults to {@link SSE_CONNECT_TIMEOUT_MS}. */
  connectTimeout?: number;
  /** When set, a `polling:stop:<eventId>` document event bails (deliberate cancel, not a failure). */
  eventId?: string | null;
  /** Overridable in tests; defaults to the production seams. */
  clientFactory?: StreamClientFactory;
}

/**
 * Records an SSE fast-path fallback to the backend client log so SSE health is
 * visible in production (queryable in the `Web` CloudWatch group). Fire-and-forget
 * and never throws — telemetry must not affect the polling flow. Only genuine SSE
 * failures are reported; flag-off / no-pollingKey opt-outs and deliberate cancels
 * are not, so the signal reflects real degradation.
 */
function reportFastPathFallback(reason: 'timeout' | 'error' | 'not-available' | 'subscribe', key: string, connected?: boolean, status?: number): void {
  try {
    // key identifies the flow (its suffix, e.g. _image_removebg) and correlates with
    // backend logs; connected (when known) splits a timeout into "gateway never
    // connected" (infra/auth) vs "connected but no event" (publish/delivery/slow job).
    // reason=subscribe carries the gateway's HTTP status: the connection was healthy
    // but the /subscribe registration was refused — alertable separately from
    // token/connect failures (reason=error).
    const connectedPart = connected === undefined ? '' : ` connected=${connected}`;
    const statusPart = status === undefined ? '' : ` status=${status}`;
    // transport tag: lets CloudWatch separate SSE vs Centrifugo health during the
    // migration (the stream-realtime observability stack filters on it).
    window.PMW.log(`stream-fastpath-fallback reason=${reason} key=${key}${connectedPart}${statusPart} transport=${window.PMW.streamTransport}`);
  } catch {
    // swallow: a telemetry failure must never break the polling fallback
  }
}

/**
 * Attempts to satisfy a polling call via the SSE gateway (fast path) instead
 * of the legacy busy-wait loop.
 *
 * Returns `{handled: true, result}` when the SSE path successfully determined
 * the job is done; returns `{handled: false}` whenever the fast path is
 * unavailable, inapplicable, or encounters any error. The caller must fall
 * back to the legacy polling loop on `{handled: false}`.
 *
 * @param initialData    - The value returned by the initial ajaxFunction call.
 *                         Must carry a string `pollingKey` field to opt in.
 * @param pollingFunction - The caller's polling function; called once after the
 *                         SSE ready event to read the final result.  Envelope
 *                         shape: `{isAvailable: boolean, data: R}`.
 * @param options        - See {@link FastPathOptions}. `timeout` is the overall SSE-wait
 *                         budget; `connectTimeout` bails fast if the gateway never connects
 *                         so the legacy fallback keeps its budget; `eventId` enables cancel;
 *                         `clientFactory` is a test seam.
 */
export async function tryStreamPollingFastPath<R>(
  initialData: unknown,
  pollingFunction: (d: unknown, immediate?: boolean) => Promise<{isAvailable: boolean; data: R}>,
  options: FastPathOptions = {}
): Promise<FastPathResult<R>> {
  const {
    timeout = DEFAULT_FAST_PATH_TIMEOUT_MS,
    connectTimeout = SSE_CONNECT_TIMEOUT_MS,
    eventId = null,
    clientFactory = defaultFactory,
  } = options;
  // Guard 1: feature flag must be enabled.
  if (!window.PMW.streamGatewayEnabled) {
    return {handled: false};
  }

  // Guard 2: initialData must carry a pollingKey to opt in.
  const pollingKey = extractPollingKey(initialData);
  if (pollingKey === null) {
    return {handled: false};
  }

  try {
    const client = getOrCreateClient(clientFactory);

    /**
     * Immediate check passed to awaitJob: if the result is already available
     * in the backend store (race-free after the initial request), resolve
     * immediately without waiting for the SSE event.
     */
    const immediateCheck = async (): Promise<JobReady | null> => {
      const envelope = await pollingFunction(initialData, true);
      if (envelope.isAvailable) {
        return {key: pollingKey, payload: envelope.data};
      }
      return null;
    };

    // Build a bail sentinel that resolves on timeout or on a stop (cancel) event.
    // Track WHY we bailed: a deliberate cancel must not be logged as an SSE failure.
    // Two timers, one budget: the connect timer bails early ONLY while still unconnected
    // (a stalled handshake), so a connection that never establishes falls back fast; once
    // connected it is a no-op and the overall timer governs the wait for the ready event.
    const effectiveConnectTimeout = Math.min(connectTimeout, timeout);
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let connectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let stopListener: (() => void) | null = null;
    let bailReason: BailReason = 'timeout';
    const bailPromise = new Promise<typeof BAIL>((resolve) => {
      timeoutId = setTimeout(() => {
        bailReason = 'timeout';
        resolve(BAIL);
      }, timeout);
      connectTimeoutId = setTimeout(() => {
        if (!client.isConnected()) {
          bailReason = 'timeout';
          resolve(BAIL);
        }
      }, effectiveConnectTimeout);
      if (eventId !== null) {
        const eventName = `polling:stop:${eventId}`;
        stopListener = (): void => {
          bailReason = 'cancel';
          resolve(BAIL);
        };
        document.addEventListener(eventName, stopListener);
      }
    });

    let ready: JobReady | typeof BAIL;
    try {
      // Wait for the live SSE ready event, the immediate check, or the bail sentinel.
      ready = await Promise.race([client.awaitJob(pollingKey, immediateCheck), bailPromise]);
    } finally {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (connectTimeoutId !== null) {
        clearTimeout(connectTimeoutId);
      }
      if (eventId !== null && stopListener !== null) {
        document.removeEventListener(`polling:stop:${eventId}`, stopListener);
      }
    }

    // Bail: drop the orphaned resolver and fall back to the legacy loop. Report only
    // a timeout (real SSE non-delivery); a cancel is the caller deliberately stopping,
    // which pollingAjaxCallAsync handles without re-entering the legacy loop.
    if (ready === BAIL) {
      client.cancelJob(pollingKey);
      if (bailReason === 'timeout') {
        reportFastPathFallback('timeout', pollingKey, client.isConnected());
      }
      return {handled: false};
    }

    // If the winning arm carried an inlined payload (either from immediateCheck or
    // from a live SSE event with payload attached), return it directly — no second
    // pollingFunction call needed on the common path.
    if (ready.payload !== null && ready.payload !== undefined) {
      return {handled: true, result: ready.payload as R};
    }

    // Rare large-payload case: PHP published payload: null (result too big for the
    // SSE event). Fetch the result once via pollingFunction. A throw here means the
    // JOB itself errored (status read rejected) — NOT an SSE failure: fall back so the
    // legacy loop re-fetches and surfaces it, without logging an SSE fallback.
    const envelope = await pollingFunction(initialData, true).catch(() => null);
    if (envelope === null) {
      return {handled: false};
    }
    if (envelope.isAvailable) {
      return {handled: true, result: envelope.data};
    }

    // SSE fired but the result still isn't in the store (race / spurious event). Fall back.
    reportFastPathFallback('not-available', pollingKey, client.isConnected());
    return {handled: false};
  } catch (e) {
    // An immediate status-read rejection means the JOB errored (the status endpoint
    // returned an error response), NOT that the SSE transport failed. Fall back to the
    // legacy loop — which re-fetches and surfaces the job error to the caller — WITHOUT
    // logging an SSE fallback. Mirrors the null-payload re-read path above. Everything
    // else (token fetch, gateway/EventSource connect, /subscribe) is real SSE degradation.
    if (e instanceof SubscribeRejectedError) {
      // connected=true by construction: /subscribe only runs after the SSE connect.
      reportFastPathFallback('subscribe', pollingKey, true, e.status);
    } else if (!(e instanceof ImmediateCheckError)) {
      reportFastPathFallback('error', pollingKey);
    }
    return {handled: false};
  }
}

/** Reset the module singleton. Only for use in unit tests. */
export function resetSingletonForTest(): void {
  singletonClient = null;
}
